/**
 * One organisation on "Onde você quer trabalhar?": its monogram, name and the
 * caller's role on one line, an action that opens the organisation itself, the
 * credits strip for the people who may see billing (`OrganisationFigures`), and
 * its campaigns beneath, one quiet row each.
 *
 * A campaign's `state`/`reach_7d` are `.optional()` on the wire schema for
 * fixtures built before those fields existed (`session.ts`'s own doc on
 * `campaignEntrySchema`); a row renders them only when `state` is present.
 */

import type { ReactNode } from 'react';

import { Link } from '@tanstack/react-router';
import { ArrowRight, ChevronRight, Eye, Megaphone, Plus } from 'lucide-react';

import { HintedIcon } from '../../../chrome/hint';
import { initials } from '../../../chrome/shell/initials';
import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { Card, CardHeader } from '../../../ui/card';
import { overviewPath } from '../../data/overview/paths';
import { lastPageOr } from '../lastPage';
import { campaignHref, organisationHref } from '../orgContext';
import type { CampaignEntry, CampaignStateValue, OrganisationEntry } from '../session';
import { sw, switcherCatalogue } from './messages';
import { OrganisationFigures } from './OrganisationFigures';
import { isOrgAdmin, roleLabel } from './role';

const STATE_LABEL: Record<CampaignStateValue, () => string> = {
  ACTIVE: () => sw('row.state.active'),
  PAUSED: () => sw('row.state.paused'),
  ARCHIVED: () => sw('row.state.archived'),
};

function CampaignRowFigures(props: { campaign: CampaignEntry }): ReactNode {
  const { campaign } = props;
  if (campaign.state === undefined) return null;
  return (
    <>
      <span className="hidden text-caption text-muted-foreground sm:inline">
        {STATE_LABEL[campaign.state]()}
      </span>
      <span
        data-numeric
        className="flex min-w-20 shrink-0 items-center justify-end gap-1 text-caption whitespace-nowrap text-muted-foreground"
      >
        <HintedIcon label={sw('row.reach')} className="size-4">
          <Eye aria-hidden className="size-4" />
        </HintedIcon>
        {campaign.reach_7d == null ? sw('row.reach.none') : compact(campaign.reach_7d, getLocale())}
      </span>
    </>
  );
}

function campaignCountLabel(count: number): string {
  return count === 0
    ? sw('card.noCampaigns')
    : switcherCatalogue.plural('card.campaigns.other', count, { n: count });
}

export function OrganisationCard(props: { organisation: OrganisationEntry }): ReactNode {
  const { organisation } = props;
  const role = roleLabel(organisation);
  const summary = [role, campaignCountLabel(organisation.campaigns.length)]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <Avatar size="sm">
          <AvatarFallback aria-hidden>{initials(organisation.tenant_name)}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 basis-40 flex-col">
          <h2 className="truncate font-medium">{organisation.tenant_name}</h2>
          <span className="text-caption text-muted-foreground">{summary}</span>
        </div>
        {organisation.tenant_wide_read && (
          <Button asChild variant="outline" size="sm">
            <Link to={organisationHref(organisation.tenant_id)}>
              {sw('card.open')}
              <ArrowRight aria-hidden className="size-4" />
            </Link>
          </Button>
        )}
      </CardHeader>
      <OrganisationFigures organisation={organisation} />
      {organisation.campaigns.length > 0 ? (
        <ul aria-label={sw('card.campaignsLabel', { organisation: organisation.tenant_name })}>
          {organisation.campaigns.map((campaign) => (
            <li key={campaign.campaign_id} className="border-b last:border-b-0">
              <Link
                to={campaignHref(campaign.campaign_id, lastPageOr(overviewPath))}
                className="flex items-center gap-3 px-4 py-2.5 outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
              >
                <Megaphone aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-body-sm">
                  {campaign.campaign_name}
                </span>
                <CampaignRowFigures campaign={campaign} />
                <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        isOrgAdmin(organisation) && (
          <Link
            to={organisationHref(organisation.tenant_id)}
            className="flex items-center justify-center gap-2 px-4 py-3 text-caption text-muted-foreground hover:bg-muted/50"
          >
            <Plus aria-hidden className="size-4" />
            {sw('card.createFirstCampaign')}
          </Link>
        )
      )}
    </Card>
  );
}
